import { useQuery } from "@apollo/client";
import React, { useContext, useEffect, useState } from "react";
import { MdArrowBack, MdChat, MdClose } from "react-icons/md";
import styled from "styled-components";
import { GET_CHAT_MESSAGES, PLAYER_CHATS } from "../../query/ChatQueries";
import bp from "../../styles/breakpoint";
import palette from "../../styles/palette";
import PlayerLink from "../Player/PlayerLink";
import { RootContext } from "../RootContext";
import Messages from "../Team/Chat/Messages";
import PostMessage from "../Team/Chat/PostMessage";

// --- Styled components ---

const FloatingButton = styled.button`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 6000;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: ${palette.primary.main};
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transition: background 0.15s;

  &:hover {
    background: ${palette.primary.dark};
  }

  @media screen and (max-width: ${bp.md}) {
    bottom: 68px;
    right: 12px;
    width: 44px;
    height: 44px;
  }
`;

const UnreadBadge = styled.span`
  position: absolute;
  top: 2px;
  right: 2px;
  width: 14px;
  height: 14px;
  background: ${palette.secondary.main};
  border-radius: 50%;
  border: 2px solid white;
`;

const Panel = styled.div`
  position: fixed;
  bottom: 84px;
  right: 24px;
  z-index: 6000;
  width: 340px;
  height: 480px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media screen and (max-width: ${bp.md}) {
    right: 8px;
    bottom: 124px;
    width: calc(100vw - 16px);
    height: 60vh;
  }
`;

const PanelHeader = styled.div`
  background: ${palette.primary.main};
  color: white;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: none;
`;

const PanelTitle = styled.span`
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const IconBtn = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 2px;
  opacity: 0.8;
  flex: none;

  &:hover {
    opacity: 1;
  }
`;

const PanelBody = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

// --- Conversation list ---

const ConvoList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 4px 0;
`;

const ConvoItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 8px 16px;
  background: none;
  border: none;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  cursor: pointer;
  text-align: left;
  gap: 10px;

  &:nth-child(even) {
    background: ${palette.background.default};
  }

  &:hover {
    background: ${palette.primary.light};
    color: ${palette.primary.contrastText};
  }
`;

const ConvoPlayerIcon = styled.span`
  pointer-events: none;
  flex: none;
`;


const UnreadDot = styled.span`
  width: 8px;
  height: 8px;
  background: ${palette.secondary.main};
  border-radius: 50%;
  flex: none;
`;

const EmptyState = styled.div`
  padding: 24px 16px;
  color: #888;
  text-align: center;
  font-size: 13px;
`;

// --- Active chat view ---

const ChatView = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 8px;
`;


// --- Main component ---

const FloatingChat: React.FC = () => {
  const { currentPlayer, currentPlayerId } = useContext(RootContext) as any;
  const [isOpen, setIsOpen] = useState(false);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);

  const { data: chatsData, refetch: refetchChats } = useQuery(PLAYER_CHATS, {
    skip: !currentPlayerId,
    variables: { playerId: currentPlayerId || -1 },
    fetchPolicy: "network-only",
    pollInterval: 30000,
  });

  const {
    data: chatData,
    startPolling,
    stopPolling,
  } = useQuery(GET_CHAT_MESSAGES, {
    skip: !activeChatId || !currentPlayerId,
    variables: { chatId: activeChatId, playerId: currentPlayerId },
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (activeChatId && currentPlayerId) {
      startPolling(3000);
    }
    return () => stopPolling();
  }, [activeChatId, startPolling, stopPolling]);

  if (!currentPlayerId) return null;

  const chats = [...(chatsData?.playerChats?.chats || [])].sort(
    (a: any, b: any) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
  const hasUnread = chats.some((c: any) => c.hasNewMessages);
  const chat = chatData?.chat;

  const handleOpen = () => {
    setIsOpen(true);
    refetchChats();
  };

  const handleClose = () => {
    setIsOpen(false);
    setActiveChatId(null);
  };

  const handleBack = () => {
    setActiveChatId(null);
    refetchChats();
  };

  const activeChatTitle = (() => {
    if (!activeChatId) return "Messages";
    if (chat?.players) {
      const others = chat.players.filter((p: any) => p.id !== currentPlayerId);
      return others.map((p: any) => p.name).join(", ") || "Chat";
    }
    return "Chat";
  })();

  return (
    <>
      <FloatingButton onClick={handleOpen}>
        <MdChat size={24} />
        {hasUnread && <UnreadBadge />}
      </FloatingButton>

      {isOpen && (
        <Panel>
          <PanelHeader>
            <PanelTitle>
              {activeChatId && (
                <IconBtn onClick={handleBack} title="Retour">
                  <MdArrowBack size={16} />
                </IconBtn>
              )}
              {activeChatTitle}
            </PanelTitle>
            <IconBtn onClick={handleClose} title="Fermer">
              <MdClose size={18} />
            </IconBtn>
          </PanelHeader>

          <PanelBody>
            {!activeChatId ? (
              <ConvoList>
                {chats.length === 0 ? (
                  <EmptyState>Aucune conversation</EmptyState>
                ) : (
                  chats.map((c: any) => {
                    const others = c.players.filter(
                      (p: any) => p.id !== currentPlayerId
                    );
                    return (
                      <ConvoItem
                        key={c.id}
                        onClick={() => setActiveChatId(c.id)}
                      >
                        {others.map((p: any) => (
                          <ConvoPlayerIcon key={p.id}>
                            <PlayerLink
                              player={p}
                              horizontal={true}
                              size="24px"
                              withMorale={false}
                              withMetaIcon={false}
                            />
                          </ConvoPlayerIcon>
                        ))}
                        {c.hasNewMessages && <UnreadDot />}
                      </ConvoItem>
                    );
                  })
                )}
              </ConvoList>
            ) : (
              <ChatView>
                {chat ? (
                  <>
                    <Messages
                      messages={chat.messages || []}
                      player={currentPlayer}
                      team={chat.team}
                    />
                    {currentPlayer && (
                      <PostMessage
                        playerId={currentPlayer.id}
                        chatId={chat.id}
                      />
                    )}
                  </>
                ) : (
                  <EmptyState>Chargement...</EmptyState>
                )}
              </ChatView>
            )}
          </PanelBody>
        </Panel>
      )}
    </>
  );
};

export { FloatingChat };
